import classNames from 'classnames';
import Immutable from 'immutable';
import _ from 'lodash';
import AbbreviationsIndicator from 'modules/Abbreviations/components/AbbreviationsIndicator';
import React, { useCallback, useMemo } from 'react';
import { ConnectDragSource } from 'react-dnd';
import { useDispatch, useSelector, batch } from 'react-redux';
import createDndScrollZone from 'components/DndScrollZone';
import LoadingOverlay from 'components/LoadingOverlay';
import {
  MIN_HEIGHT_FOR_MD_LAYOUT_SPINNER,
  MIN_HEIGHT_FOR_SM_LAYOUT_SPINNER,
  ProcessType,
  ProjectsConfig,
  AccessibilityRole,
  LAYOUT_DEFAULT_ACCESSIBILITY_ROLE,
  ProjectType,
} from 'const';
import { setLastEditedLayoutId } from 'containers/App/actions';
import * as AppSelectors from 'containers/App/selectors';
import ArtboardLayoutActions from 'containers/ArtboardLayoutActions';
import * as LayoutsActions from 'containers/Layouts/actions';
import { saveAppState } from 'containers/UndoRedoControl/actions';
import * as Models from 'models';
import { createSmallVerticalStrength } from 'utils/dndScrollZone';
import { getLayoutSpinnerSize } from 'utils/layouts/getLayoutSpinnerSize';
import { isLayoutScrollable } from 'utils/layouts/isLayoutScrollable';
import { isRowRelation } from 'utils/relations/isRowRelation';
import { sum } from 'utils/sum';
import { toPx } from 'utils/toPx';
import Column from './components/Column';
import Toolbar from './components/Toolbar';
import { useLayout } from './hooks/useLayout';
import { artboardLayoutSelector } from './selectors';
import css from './styles.module.scss';

const DnDScrollZone = createDndScrollZone<React.HTMLAttributes<HTMLElement>>('div');

type OwnProps = {
  artboardLayoutContainer?: HTMLDivElement;
  connectDragSource: ConnectDragSource;
  disableDragAndDelete?: boolean;
  groupLayoutId: string;
  isFirst: boolean;
  isSectionActive: boolean;
  layout: Models.LayoutMap;
  layoutId: string;
  sectionDisplayName: string;
  sectionName: string;
  sectionStyles: Models.MasterScreen.SectionStylesMap;
  ssi: Models.SSIMap | null;
  dataTestId: string;
};

type SelectorProps = {
  isPreviewShown: boolean;
  activeLayoutIds: Immutable.Set<string>;
  isGroupLayoutBeingUploaded: boolean;
  isLayoutBeingUploaded: boolean;
  documents: Models.CombinedDocumentsMap;
  flatColorsByLayoutId: Models.BrandColorsMap;
  isOpenToolbar: boolean;
  relationsByRegularLayoutId: Immutable.Map<string, Models.LayeredRelationsMap>;
  sectionsWidth: Models.MasterScreen.SectionsWidthMap;
  projectType: ProjectType;
};

type Props = OwnProps & SelectorProps;

const ArtboardLayout: React.FunctionComponent<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const {
    isPreviewShown,
    activeLayoutIds,
    connectDragSource,
    disableDragAndDelete,
    groupLayoutId,
    isGroupLayoutBeingUploaded,
    isLayoutBeingUploaded,
    isOpenToolbar,
    isSectionActive,
    layoutId,
    projectType,
    sectionDisplayName,
    sectionStyles,
    ssi,
    dataTestId,
  } = props;

  const {
    columnsWidth,
    container,
    editMode,
    enableEditMode,
    getMinColumnWidth,
    isEmpty,
    isExpanded,
    isReusableLayout,
    layout,
    layoutDocumentName,
    layoutDocumentNumber,
    layoutRelations,
    onResize,
    styles,
    stylesSetters,
    stylesCSS,
    toggleColumnsWidth,
    toggleExpandedMode,
    toggleLayoutRelations,
    setRowHeightSilently,
  } = useLayout(props);

  const currentAccessibilityRole = layout?.get('accessibilityRole') ?? LAYOUT_DEFAULT_ACCESSIBILITY_ROLE;

  const changeAccessibilityRole = useCallback((value: AccessibilityRole): void => {
    if (value === currentAccessibilityRole) {
      return;
    }
    const updatedLayout = layout.set('accessibilityRole', value);
    batch(() => {
      dispatch(saveAppState());
      dispatch(setLastEditedLayoutId(layoutId));
      dispatch(LayoutsActions.setLayout(updatedLayout));
    });

  }, [dispatch, layout, currentAccessibilityRole, layoutId]);

  const addLayoutColumn = useCallback((): void => {
    dispatch(LayoutsActions.addLayoutColumn(layout));
  }, [dispatch, layout]);

  const addLayoutRow = useCallback((): void => {
    dispatch(LayoutsActions.addLayoutRow(layout));
  }, [dispatch, layout]);

  const deleteLayoutColumn = useCallback((position: number): void => {
    dispatch(LayoutsActions.deleteLayoutColumn(layoutId, position));
  }, [dispatch, layout]);

  const { allowScrollableLayout } = ProjectsConfig[projectType];
  const height = layout.getIn(['styles', 'height']);
  const scale = useSelector(AppSelectors.getScale);

  const layoutRelation = layoutRelations.get(layout.get('relationId')) as Models.ColumnRelationMap;
  const layoutRelationChildIds = layoutRelation.get('relationIds');
  const firstLayoutChildRelation = layoutRelations.get(layoutRelationChildIds.first<string>()) as Models.RowRelationMap | Models.RegularRelationMap;
  const columnsCount = layoutRelationChildIds.size === 1 && isRowRelation(firstLayoutChildRelation)
    ? firstLayoutChildRelation.get('relationIds').size
    : 1;
  const stretchable = !layoutRelation.getIn(['styles', 'rowsHeight']).every(Boolean);
  const isAddingDisabled = false;
  const isScrollable = isLayoutScrollable(layout) && allowScrollableLayout && !groupLayoutId && !isExpanded;
  const isChildLayout = groupLayoutId && groupLayoutId !== layoutId;
  const clientHeight = (container.current?.clientHeight ?? 0) * scale || MIN_HEIGHT_FOR_SM_LAYOUT_SPINNER;

  return (
    <>
      <DnDScrollZone
        verticalStrength={createSmallVerticalStrength}
        className={classNames(css.ArtboardLayout, {
          [css.scrollable]: isScrollable,
          [css.stretchable]: stretchable,
          [css.insideOfSSI]: !!ssi,
        })}
        ref={container}
        style={{ height:
          (height && height > 1 && isScrollable && !ssi && !isExpanded)
            ? toPx(height)
            : 'auto',
        }}
        data-document-number={layoutDocumentNumber}
        data-testid={dataTestId}
      >
        <LoadingOverlay
          isLocked={!isChildLayout && isLayoutBeingUploaded}
          activeProcessType={clientHeight <= MIN_HEIGHT_FOR_MD_LAYOUT_SPINNER
            ? ProcessType.SAVE_LAYOUT_WITHOUT_MESSAGE
            : ProcessType.SAVE_REUSABLE_LAYOUT
          }
          spinnerSize={getLayoutSpinnerSize(clientHeight)}
        />
        <div
          className={css.columnsContainer}
          style={stylesCSS}
        >
          <Column
            activeLayoutIds={activeLayoutIds}
            columnsCount={columnsCount}
            disableCellWidthEditing={true}
            editMode={editMode}
            height={null}
            isLayoutEmpty={isEmpty}
            isOpenToolbar={isOpenToolbar}
            isReusableLayout={isReusableLayout}
            isSectionActive={isSectionActive}
            layout={layout}
            layoutId={layoutId}
            layoutRelations={layoutRelations}
            level={0}
            dataTestId={`${dataTestId}-column${0}`}
            relation={layoutRelation}
            sectionStyles={sectionStyles}
            setRowHeightSilently={setRowHeightSilently}
            showColumnResizer={false}
            ssi={ssi}
            toggleExpandedMode={toggleExpandedMode}
            toggleLayoutRelations={toggleLayoutRelations}
            width={sum(columnsWidth)}
          />
        </div>
      </DnDScrollZone>
      {
        !ssi && editMode &&
        <Toolbar
          styles={styles}
          stylesSetters={stylesSetters}
          columnsWidth={columnsWidth}
          getMinColumnWidth={getMinColumnWidth}
          isAddingDisabled={isAddingDisabled}
          projectType={projectType}
          addLayoutColumn={addLayoutColumn}
          applyColumnsWidth={toggleColumnsWidth}
          deleteLayoutColumn={deleteLayoutColumn}
          layoutId={layoutId}
          currentAccessibilityRole={currentAccessibilityRole}
          changeAccessibilityRole={changeAccessibilityRole}
        />
      }
      {
        (!ssi && !isPreviewShown) &&
        <ArtboardLayoutActions
          addingDisabled={isAddingDisabled}
          connectDragSource={connectDragSource}
          disableDragAndDelete={disableDragAndDelete}
          enableLayoutEditMode={enableEditMode}
          isBeingUploaded={isGroupLayoutBeingUploaded || isLayoutBeingUploaded}
          layout={layout}
          editMode={editMode}
          isChildLayout={isChildLayout}
          hint={!groupLayoutId && layoutDocumentName}
          handleDrag={onResize}
          height={_.round(height)}
          isResizeAvailable={isScrollable}
          sectionDisplayName={sectionDisplayName}
          addLayoutColumn={addLayoutColumn}
          addLayoutRow={addLayoutRow}
          dataTestId={dataTestId}
        />
      }
      <AbbreviationsIndicator layoutId={layoutId} />
    </>
  );
};

const ArtboardLayoutPropsWrapper: React.FunctionComponent<OwnProps> = (artboardLayoutOwnProps: OwnProps) => {
  const { groupLayoutId, layoutId } = artboardLayoutOwnProps;
  const selectorProps = useSelector<Models.AppState.StateMap, SelectorProps>(artboardLayoutSelector({ groupLayoutId, layoutId }));
  const allArtboardLayoutProps = useMemo(() => ({
    ...artboardLayoutOwnProps,
    ...selectorProps,
  }), [artboardLayoutOwnProps, selectorProps]);

  return (
    <ArtboardLayout {...allArtboardLayoutProps} />
  );
};

export default ArtboardLayoutPropsWrapper;
